// Grammaire restreinte de l'attribut `d` d'un chemin SVG : commandes
// M L H V C S Q T A Z (majuscules ou minuscules) et nombres, rien d'autre.
// Un chemin qui sort de cette grammaire est refusé (pas de SVG libre).
// Chaque nombre est borné (|n| ≤ maxCoordinate) : « 1e999 » vaut Infinity.

import { JRAF_LIMITS as L } from './limits';

const ARGS: Record<string, number> = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };
const TOKEN_RE = /\s*,?\s*([MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)/y;

export type PathDataCheck = { ok: true; commands: number } | { ok: false; reason: string };

export function checkPathData(d: string): PathDataCheck {
  const tokens: string[] = [];
  TOKEN_RE.lastIndex = 0;
  let pos = 0;
  const src = d.trim();
  while (pos < src.length) {
    TOKEN_RE.lastIndex = pos;
    const m = TOKEN_RE.exec(src);
    if (!m || m.index !== pos) {
      return { ok: false, reason: `caractère inattendu « ${src.slice(pos, pos + 8)} »` };
    }
    tokens.push(m[1]!);
    pos = TOKEN_RE.lastIndex;
    while (pos < src.length && /\s/.test(src[pos]!)) pos++;
  }
  if (tokens.length === 0) return { ok: false, reason: 'chemin vide' };
  if (!/^[Mm]$/.test(tokens[0]!)) return { ok: false, reason: 'un chemin commence par M' };

  let i = 0;
  let commands = 0;
  while (i < tokens.length) {
    const cmd = tokens[i]!;
    const n = ARGS[cmd.toLowerCase()];
    if (n === undefined) return { ok: false, reason: `commande attendue, « ${cmd} » trouvé` };
    commands++;
    i++;
    if (n === 0) continue;
    // une commande peut être répétée implicitement (« L 1 2 3 4 »)
    let count = 0;
    while (i < tokens.length && ARGS[tokens[i]!.toLowerCase()] === undefined) {
      const v = Number(tokens[i]);
      if (!Number.isFinite(v) || Math.abs(v) > L.maxCoordinate) {
        return {
          ok: false,
          reason: `nombre « ${tokens[i]} » hors de [-${L.maxCoordinate} ; ${L.maxCoordinate}]`,
        };
      }
      count++;
      i++;
    }
    if (count === 0 || count % n !== 0) {
      return {
        ok: false,
        reason: `« ${cmd} » attend ${n} nombre${n > 1 ? 's' : ''} (ou un multiple)`,
      };
    }
  }
  return { ok: true, commands };
}

/**
 * Boîte englobante approchée d'un chemin (points et points de contrôle,
 * commandes relatives comprises ; un arc compte par ses extrémités). Sert au
 * centre par défaut des agrandissements et rotations, et au contrôle
 * « sort du canevas ». `null` si le chemin est invalide.
 */
export function pathBounds(
  d: string,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (!checkPathData(d).ok) return null;
  const tokens: string[] = [];
  const src = d.trim();
  let pos = 0;
  while (pos < src.length) {
    TOKEN_RE.lastIndex = pos;
    const m = TOKEN_RE.exec(src);
    if (!m) break;
    tokens.push(m[1]!);
    pos = TOKEN_RE.lastIndex;
    while (pos < src.length && /\s/.test(src[pos]!)) pos++;
  }
  const xs: number[] = [];
  const ys: number[] = [];
  let cx = 0;
  let cy = 0;
  let sx = 0;
  let sy = 0;
  let i = 0;
  while (i < tokens.length) {
    const cmd = tokens[i++]!;
    const n = ARGS[cmd.toLowerCase()]!;
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    if (n === 0) {
      cx = sx;
      cy = sy;
      continue;
    }
    let first = true;
    while (i < tokens.length && ARGS[tokens[i]!.toLowerCase()] === undefined) {
      const a = tokens.slice(i, i + n).map(Number);
      i += n;
      const ox = rel ? cx : 0;
      const oy = rel ? cy : 0;
      const pts: [number, number][] = [];
      if (C === 'H') cx = a[0]! + ox;
      else if (C === 'V') cy = a[0]! + oy;
      else if (C === 'A') {
        cx = a[5]! + ox;
        cy = a[6]! + oy;
      } else {
        for (let k = 0; k + 1 < n; k += 2) pts.push([a[k]! + ox, a[k + 1]! + oy]);
        const last = pts[pts.length - 1]!;
        cx = last[0];
        cy = last[1];
      }
      for (const [x, y] of pts) {
        xs.push(x);
        ys.push(y);
      }
      xs.push(cx);
      ys.push(cy);
      if (C === 'M' && first) {
        sx = cx;
        sy = cy;
      }
      first = false;
    }
  }
  if (xs.length === 0) return null;
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}
